import React, { useMemo } from "react";
import AirportRunwayCard from "./AirportRunwayCard";
import { useTranslation } from "react-i18next";

export default function AirportRunways({ airport, activeRunwaysData }) {
  const { t } = useTranslation();

  const sortedRunways = useMemo(() => {
    const runways = [];
    for (const runway of airport.runways) {
      runways.push({
        heading: (runway.he_heading_degT + 180) % 360,
        length: runway.length_ft,
        width: runway.width_ft,
        ident: runway.le_ident,
        invertIdent: runway.he_ident,
        active: activeRunwaysData[runway.le_ident],
        ils: runway.le_ils,
      });
      runways.push({
        heading: runway.he_heading_degT,
        length: runway.length_ft,
        width: runway.width_ft,
        ident: runway.he_ident,
        invertIdent: runway.le_ident,
        active: activeRunwaysData[runway.he_ident],
        ils: runway.he_ils,
      });
    }

    const sortIndexes = { tailwind: 0, crosswind: 1, headwind: 2 };
    return runways.sort(
      (a, b) =>
        sortIndexes[b.active?.status] - sortIndexes[a.active?.status] ||
        (!!b.ils ? 1 : 0) - (!!a.ils ? 1 : 0) ||
        a.active?.crosswind - b.active?.crosswind
    );
  }, [airport, activeRunwaysData]);

  const variableWinds = airport.wind_direction === 0;

  return (
    <section
      className="mb-10 md:mb-16"
      aria-labelledby="runways-heading"
    >
      <div className="text-center mb-6 sm:mb-8 px-2 sm:px-0">
        <h2
          id="runways-heading"
          className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3"
        >
          {t("airport.runways")}
        </h2>

        {/* ATC notice */}
        <div className="glass-card rounded-lg px-3 sm:px-4 py-2 sm:py-3 max-w-2xl mx-auto break-words">
          <p className="text-[10px] sm:text-xs text-slate-400 leading-relaxed">
            ⚠ {t("airport.atcNotice")}
          </p>
        </div>

        {/* Variable winds banner */}
        {variableWinds && (
          <div
            className="mt-4 inline-flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg bg-aviation-amber/10 border border-aviation-amber/30 max-w-full"
            role="alert"
          >
            <span className="text-aviation-amber text-[11px] sm:text-sm font-bold text-center sm:text-left">
              {t("airport.variableWinds")}.
            </span>
            <span className="text-slate-400 text-[9px] sm:text-xs text-center sm:text-left break-words">
              {t("airport.variableWindsHelp")}
            </span>
          </div>
        )}
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 max-w-7xl mx-auto w-full"
        aria-label={t("airport.runways")}
      >
        {sortedRunways.map((runway, i) => (
          <div
            key={runway.ident}
            className={`card-enter card-enter-${Math.min(i + 1, 6)}`}
          >
            <AirportRunwayCard
              ident={runway.ident}
              active={runway.active}
              invertMark={runway.invertIdent}
              heading={runway.heading}
              length={runway.length}
              width={runway.width}
              ils={runway.ils}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
